import React, { useState, useRef, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  listConversations,
  getConversation,
  deleteConversation,
  chatWithMeetings,
  onChatResponse
} from '../graphql/chatQueries';
import './AIInsights.css';

const client = generateClient();

function AIInsights() {
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [identityId, setIdentityId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesAreaRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      setConversationsLoading(true);
      const response = await client.graphql({
        query: listConversations,
        variables: { limit: 50, cognitoIdentityId: identityId }
      });
      setConversations(response.data.listConversations.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [identityId]);

  useEffect(() => {
    const init = async () => {
      try {
        const session = await fetchAuthSession();
        setIdentityId(session.identityId);
      } catch (e) {
        console.error('Error fetching identity:', e);
      }
    };
    init();
  }, []);

  // Load conversations once identityId is available
  useEffect(() => {
    if (identityId) {
      loadConversations();
    }
  }, [identityId, loadConversations]);

  useEffect(() => {
    // Only auto-scroll if user hasn't manually scrolled up
    if (!userHasScrolled) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, userHasScrolled]);

  // Detect when user scrolls up to read history
  useEffect(() => {
    const messagesArea = messagesAreaRef.current;
    if (!messagesArea) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesArea;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setUserHasScrolled(!isNearBottom);
    };

    messagesArea.addEventListener('scroll', handleScroll);
    return () => messagesArea.removeEventListener('scroll', handleScroll);
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    setMessages([]);
    try {
      const response = await client.graphql({
        query: getConversation,
        variables: { conversationId: chat.conversationId, cognitoIdentityId: identityId }
      });
      const conv = response.data.getConversation;
      if (conv && conv.messages) {
        setMessages(conv.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          sources: m.sources || []
        })));
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleNewChat = () => {
    setSelectedChat(null);
    setMessages([]);
    setInput('');
    setError(null);
    setUserHasScrolled(false);
    inputRef.current?.focus();
  };

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    setContextMenu(null);
    try {
      await client.graphql({
        query: deleteConversation,
        variables: { conversationId: convId, cognitoIdentityId: identityId }
      });
      setConversations(prev => prev.filter(c => c.conversationId !== convId));
      if (selectedChat?.conversationId === convId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleRenameStart = (e, chat) => {
    e.stopPropagation();
    setContextMenu(null);
    setEditingTitle(chat.conversationId);
    setEditTitleValue(chat.title);
  };

  const handleRenameSubmit = async (convId) => {
    if (!editTitleValue.trim()) return;
    setEditingTitle(null);
    // Optimistic update
    setConversations(prev => prev.map(c =>
      c.conversationId === convId ? { ...c, title: editTitleValue.trim() } : c
    ));
    if (selectedChat?.conversationId === convId) {
      setSelectedChat(prev => ({ ...prev, title: editTitleValue.trim() }));
    }
    // TODO: Add a renameConversation mutation when backend supports it
  };

  const handleSend = useCallback(async (question = input) => {
    if (!question.trim() || isLoading) return;

    const userMessage = { role: 'user', content: question, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setAgentStatus(null);
    setError(null);
    setUserHasScrolled(false);

    try {
      // Send the query — Lambda returns immediately with conversationId
      const response = await client.graphql({
        query: chatWithMeetings,
        variables: {
          query: question,
          conversationId: selectedChat?.conversationId || null,
          cognitoIdentityId: identityId
        }
      });

      const data = response.data.chatWithMeetings;
      const convId = data.conversationId;

      if (!selectedChat) {
        const title = question.length > 60 ? question.substring(0, 60) + '...' : question;
        setSelectedChat({ conversationId: convId, title });
        loadConversations();
      }

      // If answer is already populated (sync fallback), display it directly
      if (data.answer) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.answer,
          timestamp: new Date().toISOString(),
          sources: data.sources || []
        }]);
        setIsLoading(false);
        return;
      }

      // Subscribe to the async response
      const sub = client.graphql({
        query: onChatResponse,
        variables: { conversationId: convId }
      }).subscribe({
        next: ({ data: subData }) => {
          const chatResponse = subData.onChatResponse;
          if (!chatResponse) return;

          if (chatResponse.answer) {
            // Final answer received
            setAgentStatus(null);
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: chatResponse.answer,
              timestamp: new Date().toISOString(),
              sources: chatResponse.sources || []
            }]);
            setIsLoading(false);
            loadConversations();
            sub.unsubscribe();
          } else if (chatResponse.status) {
            // Intermediate status update
            setAgentStatus(chatResponse.status);
          }
        },
        error: (err) => {
          console.error('Subscription error:', err);
          setAgentStatus(null);
          setError('Something went wrong with the subscription. Please try again.');
          setIsLoading(false);
        }
      });

      // Timeout fallback — if no response in 300s, stop waiting (multi-agent workflows need more time)
      setTimeout(() => {
        if (isLoading) {
          sub.unsubscribe();
          setIsLoading(false);
        }
      }, 300000);

    } catch (error) {
      console.error('Chat error:', error);
      setError('Failed to send message. Please try again.');
      setIsLoading(false);
      // Remove the user message that failed
      setMessages(prev => prev.slice(0, -1));
    }
  }, [input, selectedChat, identityId, isLoading, loadConversations]);

  const handleRetry = () => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      if (lastUserMessage) {
        handleSend(lastUserMessage.content);
      }
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const handleContextMenu = (e, chat) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, chat });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Group conversations by date
  const groupedConversations = conversations.reduce((groups, conv) => {
    const label = formatDate(conv.updatedAt || conv.createdAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(conv);
    return groups;
  }, {});

  return (
    <div className="insights-container">
      {/* Sidebar */}
      <div className="insights-sidebar">
        <div className="sidebar-header">
          <button className="new-chat-btn" onClick={handleNewChat}>
            <span className="plus-icon">+</span> New Thread
          </button>
        </div>
        <div className="sidebar-list">
          {conversationsLoading ? (
            <div className="sidebar-loading">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="sidebar-empty">No conversations yet</div>
          ) : (
            Object.entries(groupedConversations).map(([label, convs]) => (
              <div key={label}>
                <div className="sidebar-date-label">{label}</div>
                {convs.map(chat => (
                  <div
                    key={chat.conversationId}
                    className={`sidebar-item ${selectedChat?.conversationId === chat.conversationId ? 'active' : ''}`}
                    onClick={() => handleChatSelect(chat)}
                    onContextMenu={(e) => handleContextMenu(e, chat)}
                  >
                    {editingTitle === chat.conversationId ? (
                      <input
                        className="rename-input"
                        value={editTitleValue}
                        onChange={e => setEditTitleValue(e.target.value)}
                        onBlur={() => handleRenameSubmit(chat.conversationId)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameSubmit(chat.conversationId);
                          if (e.key === 'Escape') setEditingTitle(null);
                        }}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <span className="sidebar-item-title">{chat.title || 'New Chat'}</span>
                        <button
                          className="sidebar-item-menu"
                          onClick={(e) => handleContextMenu(e, chat)}
                        >⋯</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="insights-main">
        {!selectedChat && messages.length === 0 ? (
          <div className="welcome-screen">
            <h1 className="welcome-title">Meeting Notes AI</h1>
            <p className="welcome-subtitle">Ask anything about your meetings</p>
            <div className="suggested-grid">
              {[
                { icon: "🎯", text: "What are the key action items from my recent meetings?" },
                { icon: "💡", text: "What are the recurring themes across all meetings?" },
                { icon: "📊", text: "Summarize important decisions made this week" },
                { icon: "🔍", text: "Find discussions about project deadlines" }
              ].map((item, i) => (
                <button key={i} className="suggested-btn" onClick={() => handleSend(item.text)}>
                  <span className="suggested-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </button>
              ))}
            </div>
            <div className="welcome-input-wrapper">
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder="Ask about your meetings... (Shift+Enter for new line)"
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                    // Reset height after send
                    setTimeout(() => {
                      if (inputRef.current) {
                        inputRef.current.style.height = 'auto';
                      }
                    }, 0);
                  }
                }}
                rows={1}
              />
              <button
                className="send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                title="Send message (Enter)"
              >→</button>
            </div>
          </div>
        ) : (
          <div className="chat-view">
            {selectedChat && (
              <div className="chat-header">
                <h2 className="chat-title">{selectedChat.title}</h2>
              </div>
            )}
            <div className="messages-area" ref={messagesAreaRef}>
              <div className="messages-inner">
                {messages.map((msg, i) => (
                  <div key={i} className={`message ${msg.role}`}>
                    {msg.role === 'assistant' && <div className="avatar assistant-avatar">AI</div>}
                    <div className={`message-bubble ${msg.role}`}>
                      <div className="message-content">
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      <div className="message-timestamp">{formatTimestamp(msg.timestamp)}</div>
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="message-sources">
                          📄 {msg.sources.length} source{msg.sources.length !== 1 ? 's' : ''} referenced
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && <div className="avatar user-avatar">You</div>}
                  </div>
                ))}
                {isLoading && (
                  <div className="message assistant">
                    <div className="avatar assistant-avatar">AI</div>
                    <div className="message-bubble assistant">
                      {agentStatus ? (
                        <div className="agent-status">
                          <div className="status-spinner"></div>
                          <span>{agentStatus}</span>
                        </div>
                      ) : (
                        <div className="typing-indicator">
                          <span></span><span></span><span></span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {error && (
                  <div className="error-message">
                    <div className="error-content">
                      <span className="error-icon">⚠️</span>
                      <span className="error-text">{error}</span>
                    </div>
                    <button className="retry-btn" onClick={handleRetry}>
                      Retry
                    </button>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
            <div className="input-area">
              <div className="input-wrapper">
                <textarea
                  ref={inputRef}
                  className="chat-input"
                  placeholder="Ask a follow-up... (Shift+Enter for new line)"
                  value={input}
                  onChange={e => {
                    setInput(e.target.value);
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                      // Reset height after send
                      setTimeout(() => {
                        if (inputRef.current) {
                          inputRef.current.style.height = 'auto';
                        }
                      }, 0);
                    }
                  }}
                  rows={1}
                />
                <button
                  className="send-btn"
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isLoading}
                  title="Send message (Enter)"
                >→</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <button onClick={(e) => handleRenameStart(e, contextMenu.chat)}>✏️ Rename</button>
          <button className="danger" onClick={(e) => handleDelete(e, contextMenu.chat.conversationId)}>🗑️ Delete</button>
        </div>
      )}
    </div>
  );
}

export default AIInsights;
